import React, { useEffect, useState } from "react";
import {
  RefreshCw,
  Search,
  AlertCircle,
  Check,
  X,
  Settings,
  Star,
  Edit,
  Camera,
} from "lucide-react";
import { useTheme } from "@mui/material/styles";
import { base_url } from "../../config/config";
import { tokens } from "../../theme";
import axios from "axios";

const EmployeeMachineMatrix = () => {
  const theme = useTheme();
  const mode = theme.palette.mode;
  const colors = tokens(mode);

  const [employees, setEmployees] = useState([]);
  const [machines, setMachines] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);
  const [notification, setNotification] = useState({
    show: false,
    message: "",
    type: "",
  });
  const [hoveredMachine, setHoveredMachine] = useState(null);
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
  const [isImageDialogOpen, setIsImageDialogOpen] = useState(false);
  const [selectedMachine, setSelectedMachine] = useState(null);
  const [availableMachineImages, setAvailableMachineImages] = useState([]);
  const [customImageUrl, setCustomImageUrl] = useState("");
  const [fileUploadError, setFileUploadError] = useState("");

  // Base URL for API endpoints
  const baseURL = `${base_url}/v1`;

  // Add state for machine sorting
  const [sortBy, setSortBy] = useState("ranking");
  const [sortOrder, setSortOrder] = useState("asc");

  // Map of machine process types to placeholder image URLs
  const machineImageMap = {
    "CNC-Drehen":
      "https://upload.wikimedia.org/wikipedia/commons/thumb/4/4e/CNC_Drehmaschine.jpg/640px-CNC_Drehmaschine.jpg",
    "CNC-Fräsen":
      "https://upload.wikimedia.org/wikipedia/commons/thumb/0/0f/CNC_Milling.jpg/640px-CNC_Milling.jpg",
    "Konventionelles Drehen":
      "https://upload.wikimedia.org/wikipedia/commons/thumb/b/b4/Drehmaschine_im_Deutschen_Museum.jpg/640px-Drehmaschine_im_Deutschen_Museum.jpg",
    "Konventionelles Fräsen":
      "https://upload.wikimedia.org/wikipedia/commons/thumb/e/e9/Milling_machine_quorn.jpg/640px-Milling_machine_quorn.jpg",
    Schweißplatz:
      "https://upload.wikimedia.org/wikipedia/commons/thumb/b/b0/Welding_torch.jpg/640px-Welding_torch.jpg",
    Bandsäge:
      "https://cnc-heidenau.s3.eu-central-1.amazonaws.com/machines/Bandsa%CC%88ge+Nr.+1.jpeg",
    Bohrwerk:
      "https://upload.wikimedia.org/wikipedia/commons/thumb/7/7a/Radial_drilling_machine_Z3050.jpg/640px-Radial_drilling_machine_Z3050.jpg",
    Lagerarbeitsplatz:
      "https://upload.wikimedia.org/wikipedia/commons/thumb/3/3e/Automated_warehouse.jpg/640px-Automated_warehouse.jpg",
    Baugruppenmontage:
      "https://upload.wikimedia.org/wikipedia/commons/thumb/6/6c/Electrical_components_assembly.jpg/640px-Electrical_components_assembly.jpg",
    Lötarbeitsplatz:
      "https://upload.wikimedia.org/wikipedia/commons/thumb/0/0f/Wave_soldering_processes.jpg/640px-Wave_soldering_processes.jpg",
    Nutenstoßen:
      "https://cnc-heidenau.s3.eu-central-1.amazonaws.com/machines/Nutensto%C3%9Fen.jpeg",
    "Strahl und Lackierplatz":
      "https://upload.wikimedia.org/wikipedia/commons/thumb/2/23/Paint_booth.jpg/640px-Paint_booth.jpg",
  };

  // Helper function to get image URL for a machine
  const getMachineImageUrl = (machine) => {
    if (machine.image) return machine.image;

    // Find a matching image based on the machine process
    for (const processType in machineImageMap) {
      if (machine.prozess && machine.prozess.includes(processType)) {
        return machineImageMap[processType];
      }
    }

    // Default image if no match found
    return "https://upload.wikimedia.org/wikipedia/commons/thumb/c/c6/Drilling_machine.jpg/640px-Drilling_machine.jpg";
  };

  useEffect(() => {
    fetchData();
    loadMachineImages();
  }, []);

  // Load available machine images from public directory
  const loadMachineImages = async () => {
    try {
      // In a real environment, we would fetch this list from the server
      // For now, we'll use a static list based on the directory contents
      const images = [
        "CNC_Drehen_Mexpol_WZM_TUB_560_BMN.jpg",
        "CNC_Fräsen_VF4.jpg",
        "CNC_Fräsen_VF7.jpg",
        "CU_ 630_Konventionelles_Drehen.jpg",
        "Doosan_Puma_3100XLY_Cnc-Drehen.jpg",
        "FAT_700_CNC-Drehen.jpg",
        "FAT_930_CNC_Drehen.jpg",
        "Kasto _360_AU.jpg",
        "Konventionell_Fräsen_Union Bohrwerk.jpg",
        "Konventionelles_Drehen_C_10_TM.jpg",
        "Konventionelles_Drehen_TUG_40.jpg",
        "Lötarbeitsplatz-21.jpg",
        "Säge_Metallkraft_BMBS 300x320_CNC-G.jpg",
        "Sägen_Doall_CE-1216A.jpg",
        "Schweißdrehtisch3.jpg",
        "Schweißdrehtisch3_1.jpg",
        "Schweißdrehtisch4.jpg",
        "Schweißdrehtisch5.jpg",
        "Schweißdrehtisch_1_Schweißen.jpg",
        "Stoßen_Ellettra.jpg",
        "TUG_56_MN.jpg",
        "TUG_56_MN_CNC_Drehen.jpg",
      ];
      setAvailableMachineImages(images);
    } catch (error) {
      console.error("Error loading machine images:", error);
    }
  };

  // Open image selection dialog
  const openImageDialog = (machine) => {
    setSelectedMachine(machine);
    setCustomImageUrl(machine.image || "");
    setIsImageDialogOpen(true);
  };

  // Close image selection dialog
  const closeImageDialog = () => {
    setIsImageDialogOpen(false);
    setSelectedMachine(null);
    setCustomImageUrl("");
    setFileUploadError("");
  };

  // Update machine image
  const updateMachineImage = async (imagePath) => {
    if (!selectedMachine) return;

    try {
      const response = await axios.put(
        `${baseURL}/UpdateMachineImage/${selectedMachine._id}`,
        { imagePath },
      );

      if (response.data.status === 1) {
        // Update local machine data
        const updatedMachines = machines.map((m) =>
          m._id === selectedMachine._id
            ? { ...m, image: imagePath, imageUrl: imagePath }
            : m,
        );
        setMachines(updatedMachines);

        setNotification({
          show: true,
          message: "Machine image updated successfully",
          type: "success",
        });
        setTimeout(
          () => setNotification({ show: false, message: "", type: "" }),
          3000,
        );
        closeImageDialog();
      } else {
        setNotification({
          show: true,
          message: response.data.message || "Failed to update machine image",
          type: "error",
        });
        setTimeout(
          () => setNotification({ show: false, message: "", type: "" }),
          3000,
        );
      }
    } catch (error) {
      console.error("Error updating machine image:", error);
      setNotification({
        show: true,
        message: "Error updating machine image",
        type: "error",
      });
      setTimeout(
        () => setNotification({ show: false, message: "", type: "" }),
        3000,
      );
    }
  };

  const fetchData = async () => {
    setLoading(true);
    setError("");
    try {
      const [empRes, macRes, assignRes] = await Promise.all([
        fetch(`${baseURL}/getAllEmployees`).then((res) => res.json()),
        fetch(`${baseURL}/getAllMachines`).then((res) => res.json()),
        fetch(`${baseURL}/getAllAssignments`).then((res) => res.json()),
      ]);
      setEmployees(empRes);

      // Add image URLs to machines
      const machinesWithImages = macRes.map((machine) => ({
        ...machine,
        imageUrl: getMachineImageUrl(machine),
      }));

      // Using live data from the API with added image URLs
      setMachines(sortMachines(machinesWithImages));

      // Process assignments to ensure isMainSkill is a boolean
      const processedAssignments = assignRes.map((assignment) => ({
        ...assignment,
        // If isMainSkill is undefined/null, set it to false
        isMainSkill: assignment.isMainSkill === true,
      }));

      setAssignments(processedAssignments);

      console.log("Fetched assignments:", processedAssignments);

      setNotification({
        show: true,
        message: "Data refreshed successfully",
        type: "success",
      });
      setTimeout(
        () => setNotification({ show: false, message: "", type: "" }),
        3000,
      );
    } catch (err) {
      console.error("Error fetching data: ", err);
      setError("Failed to fetch data. Please try again later.");
    }
    setLoading(false);
  };

  // Function to sort machines
  const sortMachines = (machineList) => {
    return [...machineList].sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];

      // Handle null values
      if (aValue === null) aValue = sortBy === "ranking" ? Infinity : "";
      if (bValue === null) bValue = sortBy === "ranking" ? Infinity : "";

      // Compare based on sort order
      if (sortOrder === "asc") {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  };

  // Update sorting
  const updateSort = (field) => {
    if (sortBy === field) {
      // Toggle sort order if clicking the same field
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      // Set new sort field and default to ascending
      setSortBy(field);
      setSortOrder("asc");
    }

    // Re-sort machines with new criteria
    setMachines(sortMachines(machines));
  };

  // Handle mouse enter on machine header
  const handleMachineMouseEnter = (machine, event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setPopupPosition({
      top: rect.bottom + window.scrollY + 10,
      left: rect.left + window.scrollX - 100 + rect.width / 2,
    });
    setHoveredMachine(machine);
  };

  // Handle mouse leave on machine header
  const handleMachineMouseLeave = () => {
    setHoveredMachine(null);
  };

  // Check if a given employee-machine combination is assigned
  const isAssigned = (employeeId, machineId) => {
    return assignments.some(
      (assignment) =>
        assignment?.employee?._id === employeeId &&
        assignment?.machine?._id === machineId,
    );
  };

  // Check if a given employee-machine assignment is a main skill
  const isMainSkill = (employeeId, machineId) => {
    const assignment = assignments.find(
      (assignment) =>
        assignment?.employee?._id === employeeId &&
        assignment?.machine?._id === machineId,
    );
    // Explicitly check if isMainSkill is true, since it might be undefined in the response
    return assignment && assignment.isMainSkill === true;
  };

  // Toggle assignment: delete if exists, otherwise create a new assignment
  const toggleAssignment = async (employeeId, machineId) => {
    setIsUpdating(true);
    const existingAssignment = assignments.find((assignment) => {
      const empId = assignment?.employee?._id ?? assignment?.employee;
      const machId = assignment?.machine?._id ?? assignment?.machine;
      return empId === employeeId && machId === machineId;
    });

    try {
      if (existingAssignment) {
        // Remove the assignment
        await fetch(`${baseURL}/deleteAssignment/${existingAssignment._id}`, {
          method: "DELETE",
        });
        setNotification({
          show: true,
          message: "Assignment removed successfully",
          type: "success",
        });
      } else {
        // Create a new assignment
        await fetch(`${baseURL}/createAssignment`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            employee: employeeId,
            machine: machineId,
            isMainSkill: false, // Default to not main skill
          }),
        });
        setNotification({
          show: true,
          message: "Assignment created successfully",
          type: "success",
        });
      }
      // Refresh data after updating the assignment
      await fetchData();
    } catch (err) {
      console.error("Error updating assignment: ", err);
      setNotification({
        show: true,
        message: "Error updating assignment. Please try again.",
        type: "error",
      });
    }
    setIsUpdating(false);
    setTimeout(
      () => setNotification({ show: false, message: "", type: "" }),
      3000,
    );
  };

  // Toggle the main skill status
  const toggleMainSkill = async (employeeId, machineId) => {
    setIsUpdating(true);
    const existingAssignment = assignments.find((assignment) => {
      const empId = assignment?.employee?._id ?? assignment?.employee;
      const machId = assignment?.machine?._id ?? assignment?.machine;
      return empId === employeeId && machId === machineId;
    });

    if (!existingAssignment) {
      setNotification({
        show: true,
        message: "Cannot set main skill: Assignment does not exist",
        type: "error",
      });
      setIsUpdating(false);
      setTimeout(
        () => setNotification({ show: false, message: "", type: "" }),
        3000,
      );
      return;
    }

    // Define the new isMainSkill value explicitly
    // If the field doesn't exist in the assignment object, treat it as false
    const currentMainSkill = existingAssignment.isMainSkill === true;
    const newMainSkillValue = !currentMainSkill;

    try {
      console.log("Updating assignment:", existingAssignment._id);
      console.log("Current isMainSkill:", currentMainSkill);
      console.log("Setting isMainSkill to:", newMainSkillValue);

      // Create a direct axios request to ensure proper JSON handling
      const response = await axios.put(
        `${baseURL}/updateAssignment/${existingAssignment._id}`,
        {
          isMainSkill: newMainSkillValue,
          // Include the IDs to ensure they're preserved
          employee: existingAssignment.employee._id,
          machine: existingAssignment.machine._id,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      console.log("API response:", response.data);

      if (response.status !== 200) {
        throw new Error("Failed to update main skill status");
      }

      // Force update the local assignments immediately regardless of response
      const updatedAssignments = assignments.map((assignment) =>
        assignment._id === existingAssignment._id
          ? {
              ...assignment,
              isMainSkill: newMainSkillValue,
            }
          : assignment,
      );
      setAssignments(updatedAssignments);

      setNotification({
        show: true,
        message: currentMainSkill
          ? "Removed as main skill"
          : "Set as main skill",
        type: "success",
      });

      // Since there might be issues with the backend response, wait a moment before refreshing
      setTimeout(async () => {
        await fetchData();
      }, 500);
    } catch (err) {
      console.error("Error updating main skill:", err);
      setNotification({
        show: true,
        message: `Error updating main skill: ${err.message}`,
        type: "error",
      });
    }
    setIsUpdating(false);
    setTimeout(
      () => setNotification({ show: false, message: "", type: "" }),
      3000,
    );
  };

  // Filter employees based on search term
  const filteredEmployees = employees.filter((employee) =>
    employee.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  // ─── Mode-aware semantic colors ──────────────────────────────────────
  const isDark = mode === "dark";
  // Surfaces
  const surfaceBg = isDark ? colors.primary[300] : "#ffffff"; // table cells / dialogs
  const headerBg = isDark ? colors.primary[500] : colors.primary[900]; // #d0d1d5 in light = soft grey
  const borderColor = isDark ? colors.grey[700] : colors.grey[700]; // #3d3d3d dark / #a3a3a3 light
  // Text
  const textPrimary = isDark ? colors.grey[100] : colors.grey[100]; // both modes: high-contrast text
  const textMuted = isDark ? colors.grey[300] : colors.grey[300];
  // Inputs / buttons
  const inputBg = isDark ? colors.primary[400] : "#f5f5f5";
  const inputBorder = isDark ? colors.grey[600] : colors.grey[600]; // #525252 dark / #858585 light
  const inactiveBtnBg = isDark ? colors.grey[700] : colors.grey[800]; // light: #c2c2c2
  const inactiveBtnColor = isDark ? colors.grey[100] : colors.grey[100];

  // ─── Styles (mirrors EmployeeSkillsMatrix) ───────────────────────────
  const containerStyle = {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    height: "100%",
    boxSizing: "border-box",
    padding: "20px",
    overflow: "hidden",
    gap: "12px",
  };

  const tableWrapperStyle = {
    flex: 1,
    overflowX: "auto",
    overflowY: "auto",
    border: `1px solid ${borderColor}`,
    borderRadius: "4px",
  };

  const tableStyle = {
    borderCollapse: "collapse",
    minWidth: "600px",
    width: "100%",
    backgroundColor: surfaceBg,
  };

  const thTdStyle = {
    border: `1px solid ${borderColor}`,
    padding: "8px",
    textAlign: "center",
    wordWrap: "break-word",
    color: textPrimary,
  };

  const headerStyle = {
    ...thTdStyle,
    position: "sticky",
    top: 0,
    backgroundColor: headerBg,
    zIndex: 100,
    fontWeight: "bold",
  };

  const employeeNameStyle = {
    fontWeight: "bold",
    position: "sticky",
    left: 0,
    backgroundColor: headerBg,
    zIndex: 99,
    color: textPrimary,
    textAlign: "left",
    whiteSpace: "nowrap",
    padding: "8px 16px",
  };

  const headerEmployeeNameStyle = {
    ...headerStyle,
    ...employeeNameStyle,
    zIndex: 101,
  };

  const toolbarStyle = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "10px",
    padding: "10px 4px",
  };

  const inputStyle = {
    padding: "6px 10px 6px 32px",
    backgroundColor: inputBg,
    color: textPrimary,
    border: `1px solid ${inputBorder}`,
    borderRadius: "4px",
    outline: "none",
    fontSize: "13px",
  };

  const sortBtnBase = {
    padding: "4px 12px",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "12px",
    border: "none",
    marginLeft: "6px",
  };

  const sortBtnActive = {
    ...sortBtnBase,
    backgroundColor: colors.blue[400],
    color: "#fff",
  };

  const sortBtnInactive = {
    ...sortBtnBase,
    backgroundColor: inactiveBtnBg,
    color: inactiveBtnColor,
  };

  const assignBtnBase = {
    width: 34,
    height: 34,
    borderRadius: "50%",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background 0.2s",
  };

  // ─── Popup overlay styles ──────────────────────────────────────────────
  const popupStyle = {
    position: "fixed",
    zIndex: 1200,
    padding: "16px",
    backgroundColor: surfaceBg,
    border: `1px solid ${borderColor}`,
    borderRadius: "8px",
    boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
    maxWidth: 360,
    top: popupPosition.top,
    left: popupPosition.left,
    transform: "translateX(-50%)",
    color: textPrimary,
  };

  const dialogStyle = {
    backgroundColor: surfaceBg,
    border: `1px solid ${borderColor}`,
    borderRadius: "8px",
    maxWidth: 900,
    width: "90%",
    maxHeight: "90vh",
    overflow: "auto",
    color: textPrimary,
  };

  const dialogOverlayStyle = {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(0,0,0,0.6)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1300,
  };

  // ─── Render ────────────────────────────────────────────────────────────
  return (
    <div style={containerStyle}>
      {/* Notification toast */}
      {notification.show && (
        <div
          style={{
            position: "fixed",
            top: 16,
            right: 16,
            zIndex: 1400,
            padding: "12px 20px",
            borderRadius: 8,
            display: "flex",
            alignItems: "center",
            gap: 8,
            backgroundColor:
              notification.type === "success"
                ? colors.greenAccent[600]
                : colors.redAccent[500],
            color: "#fff",
            boxShadow: "0 4px 16px rgba(0,0,0,0.4)",
          }}
        >
          {notification.type === "success" ? (
            <Check size={18} />
          ) : (
            <AlertCircle size={18} />
          )}
          <span style={{ fontSize: 14 }}>{notification.message}</span>
        </div>
      )}

      {/* Machine image hover popup */}
      {hoveredMachine && (
        <div style={popupStyle}>
          <h3 style={{ margin: "0 0 6px", color: textPrimary, fontSize: 15 }}>
            {hoveredMachine.name} • {hoveredMachine.prozess}
          </h3>
          {hoveredMachine.manufacture && (
            <p
              style={{
                margin: "0 0 8px",
                color: textMuted,
                fontSize: 13,
              }}
            >
              {hoveredMachine.manufacture}
              {hoveredMachine.typeOfMachine
                ? ` • ${hoveredMachine.typeOfMachine}`
                : ""}
            </p>
          )}
          <div style={{ position: "relative" }}>
            <img
              src={hoveredMachine.imageUrl}
              alt={hoveredMachine.prozess}
              style={{
                width: "100%",
                height: 200,
                objectFit: "cover",
                borderRadius: 6,
              }}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src =
                  "https://upload.wikimedia.org/wikipedia/commons/thumb/d/d8/Factory-icon.svg/640px-Factory-icon.svg.png";
              }}
            />
            <button
              onClick={(e) => {
                e.stopPropagation();
                openImageDialog(hoveredMachine);
              }}
              style={{
                position: "absolute",
                bottom: 8,
                right: 8,
                background: "rgba(255,255,255,0.85)",
                border: "none",
                borderRadius: "50%",
                padding: 6,
                cursor: "pointer",
              }}
              title="Edit image"
            >
              <Edit size={16} color="#1565c0" />
            </button>
          </div>
          <div style={{ marginTop: 6, fontSize: 12, color: textMuted }}>
            Ranking: {hoveredMachine.ranking || "N/A"}
          </div>
        </div>
      )}

      {/* ── Toolbar ─────────────────────────────── */}
      <div style={toolbarStyle}>
        {/* Left: title + search + refresh */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <span style={{ fontSize: 18, fontWeight: 700, color: textPrimary }}>
            Mitarbeiter-Maschinen-Matrix
          </span>

          {/* Search */}
          <div style={{ position: "relative" }}>
            <input
              type="text"
              placeholder="Mitarbeiter suchen..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={inputStyle}
            />
            <Search
              size={14}
              style={{
                position: "absolute",
                left: 10,
                top: "50%",
                transform: "translateY(-50%)",
                color: textMuted,
              }}
            />
          </div>

          {/* Refresh */}
          <button
            onClick={fetchData}
            disabled={loading}
            style={{
              ...sortBtnInactive,
              display: "flex",
              alignItems: "center",
              gap: 6,
              opacity: loading ? 0.5 : 1,
            }}
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            Aktualisieren
          </button>
        </div>

        {/* Right: sort + stats + legend */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          {/* Sort buttons */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              fontSize: 13,
              color: textMuted,
            }}
          >
            Sortierung:
            {[
              ["ranking", "Ranking"],
              ["prozess", "Prozess"],
              ["name", "Nr."],
            ].map(([field, label]) => (
              <button
                key={field}
                onClick={() => updateSort(field)}
                style={sortBy === field ? sortBtnActive : sortBtnInactive}
              >
                {label}{" "}
                {sortBy === field ? (sortOrder === "asc" ? "↑" : "↓") : ""}
              </button>
            ))}
          </div>

          {/* Stats */}
          <span style={{ fontSize: 12, color: textMuted }}>
            {machines.length} Maschinen &nbsp;•&nbsp;{" "}
            {machines.filter((m) => m.prozess?.includes("CNC")).length} CNC
          </span>

          {/* Legend */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              fontSize: 12,
              color: textMuted,
            }}
          >
            <span>Legende:</span>
            <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <span
                style={{
                  width: 14,
                  height: 14,
                  borderRadius: 3,
                  backgroundColor: inactiveBtnBg,
                  display: "inline-block",
                }}
              />
              Nicht zugewiesen
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <span
                style={{
                  width: 14,
                  height: 14,
                  borderRadius: 3,
                  backgroundColor: colors.greenAccent[600],
                  display: "inline-block",
                }}
              />
              Zugewiesen
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <span
                style={{
                  width: 14,
                  height: 14,
                  borderRadius: 3,
                  backgroundColor: colors.blue[400],
                  display: "inline-block",
                }}
              />
              Hauptskill
            </span>
          </div>
        </div>
      </div>

      {/* ── Main table area ──────────────────────── */}
      <div style={tableWrapperStyle}>
        {loading && !error ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 200,
              color: textPrimary,
            }}
          >
            <RefreshCw
              size={32}
              style={{ animation: "spin 1s linear infinite", marginRight: 12 }}
            />
            Lade Daten...
          </div>
        ) : error ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              height: 200,
              color: colors.redAccent[400],
            }}
          >
            <AlertCircle size={48} style={{ marginBottom: 12 }} />
            <p>{error}</p>
            <button
              onClick={fetchData}
              style={{ ...sortBtnInactive, marginTop: 12 }}
            >
              Erneut versuchen
            </button>
          </div>
        ) : employees.length === 0 || machines.length === 0 ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 200,
              color: textMuted,
            }}
          >
            Keine Mitarbeiter- oder Maschinendaten verfügbar.
          </div>
        ) : (
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={headerEmployeeNameStyle}>Mitarbeiter</th>
                {machines.map((machine) => (
                  <th key={machine._id} style={headerStyle}>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        position: "relative",
                        cursor: "pointer",
                        minWidth: 90,
                      }}
                      onMouseEnter={(e) => handleMachineMouseEnter(machine, e)}
                      onMouseLeave={handleMachineMouseLeave}
                    >
                      {/* Ranking badge */}
                      <span
                        style={{
                          position: "absolute",
                          top: -4,
                          right: -4,
                          background: colors.blue[400],
                          color: "#fff",
                          fontSize: 10,
                          borderRadius: "50%",
                          width: 18,
                          height: 18,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {machine.ranking || "-"}
                      </span>

                      {/* Icon + camera button */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                          marginBottom: 2,
                        }}
                      >
                        <Settings size={16} />
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openImageDialog(machine);
                          }}
                          style={{
                            background: colors.blue[700],
                            border: "none",
                            borderRadius: "50%",
                            padding: 3,
                            cursor: "pointer",
                            display: "flex",
                          }}
                          title="Bild bearbeiten"
                        >
                          <Camera size={12} color={colors.grey[100]} />
                        </button>
                      </div>

                      {/* Process name */}
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: "normal",
                          color: textMuted,
                          textTransform: "none",
                          maxWidth: 110,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={machine.prozess}
                      >
                        {machine.prozess}
                      </span>

                      {/* Manufacturer */}
                      {machine.manufacture && (
                        <span
                          style={{
                            fontSize: 10,
                            fontWeight: "normal",
                            color: textMuted,
                            textTransform: "none",
                            maxWidth: 110,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={`${machine.manufacture} ${machine.typeOfMachine || ""}`}
                        >
                          {machine.manufacture}
                          {machine.typeOfMachine
                            ? ` • ${machine.typeOfMachine}`
                            : ""}
                        </span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredEmployees.map((employee) => (
                <tr
                  key={employee._id}
                  style={{ transition: "background 0.15s" }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = headerBg)
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = "")
                  }
                >
                  <td style={{ ...thTdStyle, ...employeeNameStyle }}>
                    {employee.name}
                  </td>
                  {machines.map((machine) => {
                    const assigned = isAssigned(employee._id, machine._id);
                    const mainSkill = isMainSkill(employee._id, machine._id);
                    return (
                      <td key={machine._id} style={thTdStyle}>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 4,
                          }}
                        >
                          <div style={{ display: "flex", gap: 4 }}>
                            {/* Assign / unassign button */}
                            <button
                              onClick={() =>
                                toggleAssignment(employee._id, machine._id)
                              }
                              disabled={isUpdating}
                              title={
                                assigned ? "Zuweisung aufheben" : "Zuweisen"
                              }
                              style={{
                                ...assignBtnBase,
                                backgroundColor: mainSkill
                                  ? colors.blue[600]
                                  : assigned
                                    ? colors.greenAccent[600]
                                    : inactiveBtnBg,
                                opacity: isUpdating ? 0.5 : 1,
                              }}
                            >
                              {assigned ? (
                                <Check size={16} color="#fff" />
                              ) : (
                                <X size={16} color={textMuted} />
                              )}
                            </button>

                            {/* Main-skill toggle */}
                            {assigned && (
                              <button
                                onClick={() =>
                                  toggleMainSkill(employee._id, machine._id)
                                }
                                disabled={isUpdating}
                                title={
                                  mainSkill
                                    ? "Hauptskill entfernen"
                                    : "Als Hauptskill setzen"
                                }
                                style={{
                                  ...assignBtnBase,
                                  backgroundColor: mainSkill
                                    ? colors.blue[600]
                                    : inactiveBtnBg,
                                  opacity: isUpdating ? 0.5 : 1,
                                }}
                              >
                                <Star
                                  size={16}
                                  color={mainSkill ? "#fff" : textMuted}
                                  fill={mainSkill ? "#fff" : "none"}
                                />
                              </button>
                            )}
                          </div>

                          {assigned && mainSkill && (
                            <span
                              style={{
                                fontSize: 10,
                                padding: "1px 6px",
                                backgroundColor: colors.blue[700],
                                color: "#fff",
                                borderRadius: 4,
                              }}
                            >
                              Hauptskill
                            </span>
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Image selection dialog ───────────────── */}
      {isImageDialogOpen && selectedMachine && (
        <div style={dialogOverlayStyle}>
          <div style={dialogStyle}>
            {/* Dialog header */}
            <div
              style={{
                padding: "16px 20px",
                borderBottom: `1px solid ${borderColor}`,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <h2 style={{ margin: 0, fontSize: 17, color: textPrimary }}>
                Maschinenbild ändern: {selectedMachine.name}
              </h2>
              <button
                onClick={closeImageDialog}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: textMuted,
                }}
              >
                <X size={22} />
              </button>
            </div>

            <div
              style={{
                padding: 20,
                display: "flex",
                flexDirection: "column",
                gap: 20,
              }}
            >
              {/* Current image */}
              <div>
                <h3
                  style={{
                    margin: "0 0 8px",
                    fontSize: 14,
                    color: textMuted,
                  }}
                >
                  Aktuelles Bild
                </h3>
                <img
                  src={
                    selectedMachine.imageUrl ||
                    "https://upload.wikimedia.org/wikipedia/commons/thumb/c/c6/Drilling_machine.jpg/640px-Drilling_machine.jpg"
                  }
                  alt={selectedMachine.name}
                  style={{
                    width: "100%",
                    height: 180,
                    objectFit: "cover",
                    borderRadius: 6,
                    border: `1px solid ${borderColor}`,
                  }}
                />
              </div>

              {/* Custom URL */}
              <div>
                <h3
                  style={{
                    margin: "0 0 8px",
                    fontSize: 14,
                    color: textMuted,
                  }}
                >
                  Benutzerdefinierte URL
                </h3>
                <div style={{ display: "flex", gap: 8 }}>
                  <input
                    type="text"
                    value={customImageUrl}
                    onChange={(e) => setCustomImageUrl(e.target.value)}
                    placeholder="Bild-URL eingeben"
                    style={{ ...inputStyle, flex: 1 }}
                  />
                  <button
                    onClick={() => updateMachineImage(customImageUrl)}
                    disabled={!customImageUrl}
                    style={{
                      ...sortBtnActive,
                      opacity: !customImageUrl ? 0.5 : 1,
                    }}
                  >
                    Übernehmen
                  </button>
                </div>
              </div>

              {/* Image grid */}
              <div>
                <h3
                  style={{
                    margin: "0 0 10px",
                    fontSize: 14,
                    color: textMuted,
                  }}
                >
                  Verfügbare Bilder
                </h3>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      "repeat(auto-fill, minmax(140px, 1fr))",
                    gap: 10,
                  }}
                >
                  {availableMachineImages.map((image, index) => {
                    const imagePath = `/machines/${image}`;
                    return (
                      <div
                        key={index}
                        onClick={() => updateMachineImage(imagePath)}
                        style={{
                          cursor: "pointer",
                          border: `2px solid ${borderColor}`,
                          borderRadius: 6,
                          overflow: "hidden",
                          position: "relative",
                          transition: "border-color 0.2s",
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.borderColor = colors.blue[500])
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.borderColor = borderColor)
                        }
                      >
                        <img
                          src={imagePath}
                          alt={`Maschine ${index + 1}`}
                          style={{
                            width: "100%",
                            height: 100,
                            objectFit: "cover",
                            display: "block",
                          }}
                        />
                        <div
                          style={{
                            position: "absolute",
                            inset: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            opacity: 0,
                            backgroundColor: "rgba(0,0,0,0.4)",
                            transition: "opacity 0.2s",
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.opacity = 1)
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.opacity = 0)
                          }
                        >
                          <Check size={24} color="#fff" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeMachineMatrix;
